import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import {
  getLeafNodesValueKeys,
  getElementFilterValue,
  getElementNodes,
} from "Course/modules/Utils";
import { isSubjectStandard } from "./Utils";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";
import { getCourseOverviewDownloadQuery } from "./Queries";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import {
  removeProgressElement,
  setProgress,
  downloadFromUrl,
} from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { generateRandomId, getArchivedSortedSubjectListMemoized } from "Utils";
import moment from "moment";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

export const NAME = "courseOverview";

export const UPDATE_FILTERS = "UPDATE_FILTERS" + " " + NAME;

export const updateFilters = data => {
  return { type: UPDATE_FILTERS, data: data };
};

const HEADER_DATA = "HEADER_DATA";
const BODY_DATA = "BODY_DATA";

// since remaining and covered element structure is different
// for PYP and rest of the curriculums
// this function extracts remaining and covered element nodes for both of them
const getRemainingAndCoveredElements = ({
  yearlyInsight,
  curriculumType,
  elementType,
}) => {
  // for PYP the structure is
  //  elementType
  //    elementsNotCovered
  //    elementsCovered
  if (_.isEqual(curriculumType, CURRICULUM_TYPE_PYP)) {
    const pypYearlyInsight = _.find(yearlyInsight, { type: elementType });
    return {
      // HACK: solve levelId conflicts by aliasing field in GQL
      remainingElement: _.has(pypYearlyInsight, "elementsNotCovered_")
        ? _.get(pypYearlyInsight, "elementsNotCovered_")
        : _.get(pypYearlyInsight, "elementsNotCovered"),
      coveredElement: _.get(pypYearlyInsight, "elementsCovered"),
    };
    // for other curriculums, its opposite
    //  elementSetsNotCovered
    //    elementType
    //  elementSetsCovered
    //    elementType
  } else {
    return {
      remainingElement: _.find(
        _.get(yearlyInsight, "elementSetsNotCovered", []),
        {
          type: elementType,
        }
      ),
      coveredElement: _.find(_.get(yearlyInsight, "elementSetsCovered", []), {
        type: elementType,
      }),
    };
  }
};

// this function returns element nodes based on curriculum type
// if the planner element is dependent on subject, it returns only that subject's nodes
const getPlannerElementNodes = ({
  elementSet,
  subject,
  curriculumType,
  isInterdisciplinary = false,
}) => {
  // for interdisciplinary and standards fields
  // filter out nodes subject wise
  if (_.isEqual(curriculumType, CURRICULUM_TYPE_PYP)) {
    const elementNodes = _.get(elementSet, "elements", []);

    return isInterdisciplinary
      ? _.filter(elementNodes, node => {
          const nodeSubject = node?.subject;
          return _.isEqual(nodeSubject, subject.id);
        })
      : elementNodes;
  } else {
    // need the whole plannerElementNode here instead of just id
    // because might need to further filter out based on isLeaf
    return isInterdisciplinary
      ? getElementNodes({ elementSet, subject })
      : _.get(elementSet, "plannerElementNode", []);
  }
};

/**
 * This function returns a custom object having relevant data for
 * insight of a Planner Element Data.
 *
 * @param {Object} param0
 * @param {Object} param0.itemObj - insight item object (from courseOverview array from template)
 * @param {Function} param0.valueFilter - element value filter function
 * @param {Array} param0.allPYPNodeIds - all node ids of pyp data
 * @param {String} param0.curriculumType - curriculum is pyp, myp, etc.
 * @param {Object} param0.yearlySchedule - yearly schedule of selected course
 */

const getInsightPlannerElementData = ({
  itemObj,
  fieldObj,
  valueFilter,
  subjectIds,
  unitPlan,
}) => {
  let value = null;
  let valueCount = null;
  const fields = _.get(unitPlan, "fields", []);

  const fieldUID = _.get(itemObj, "fieldUID", "");

  let valueKeys = _.get(
    _.find(fields, field => _.get(field, "uid") === fieldUID),
    "value",
    []
  );

  const resolvedValue = _.get(
    _.find(fields, field => _.get(field, "uid") === fieldUID),
    "resolvedMinimalTree",
    {}
  );

  // need to display as text for certain views
  if (_.includes(["GroupedConceptsView"], _.get(fieldObj, "viewType", ""))) {
    fieldObj["viewType"] = "TextView";
  }

  const viewType = _.get(fieldObj, "viewType", "");
  const id = _.get(unitPlan, "id", "");
  valueKeys = valueKeys ? valueKeys : [];

  valueCount = _.size(valueKeys);
  value = getElementFilterValue({
    valueKeys: valueKeys,
    fieldUID,
    fieldObj,
    valueFilter,
    resolvedValue,
    subjectIds,
  });

  return {
    viewType,
    value,
    valueCount,
    id,
  };
};

export const getElementFilterValueMemoize = _.memoize(
  getElementFilterValue,
  params => JSON.stringify(params)
);

export const getLeafNodesValueKeysMemoize = _.memoize(
  getLeafNodesValueKeys,
  params => JSON.stringify(params)
);

/**
 * This function generates and returns the grid data unit wise for each subject.
 *
 * @param {Object} param0
 * @param {Object} param0.itemObj - insight item object (from courseOverview array from template)
 * @param {Array} param0.subjects - passed subject list
 * @param {Object} param0.unitPlan - passed unit plan
 * @param {String} param0.elementType - element type to be displayed
 * @param {Object} param0.yearlyInsight - myp insight of course
 * @param {Array} param0.yearlySchedule - yearly schedule of course
 */

const getInterdisciplinaryFieldsData = ({
  itemObj,
  fieldObj,
  subjects,
  unitPlan,
  elementType,
  yearlyInsight,
  curriculumType,
  tableDataType,
  canEdit,
}) => {
  return _.map(subjects, subject => {
    const { remainingElement, coveredElement } = getRemainingAndCoveredElements(
      {
        elementType,
        curriculumType,
        yearlyInsight,
      }
    );

    let label = _.get(itemObj, "label", "");
    const subjectName = _.get(subject, "name", "");
    const subjectId = _.get(subject, "id", "");

    const unitPlanSubjects = _.get(unitPlan, "subjects.value", []);

    let fieldUID = _.get(itemObj, "fieldUID", "");
    let updatedItemObj = itemObj;

    if (_.has(itemObj, "interdisciplinaryFields")) {
      const interdisciplinaryFields = itemObj.interdisciplinaryFields;
      const interdisciplinaryUID = `${itemObj.interdisciplinaryUID}_${subjectId}`;

      if (
        _.includes(interdisciplinaryFields, interdisciplinaryUID) &&
        _.isEqual(tableDataType, BODY_DATA) &&
        _.isEqual(
          _.get(unitPlan, "unitType.value", ""),
          "ibMYPInterdisciplinary"
        )
      ) {
        fieldUID = interdisciplinaryUID;
        updatedItemObj = {
          ..._.cloneDeep(itemObj),
          fieldUID: interdisciplinaryUID,
        };
      }
    }

    const viewType = _.get(fieldObj, "viewType", "");

    // prefix subject name to standards but we don't do that for DP
    if (
      isSubjectStandard({ elementType }) &&
      !_.isEqual(elementType, "DP_SUBJECT_STANDARD")
    )
      label = `${subjectName} ${label}`;
    let remainingElementNodes = [],
      remainingValue;

    let remainingCount = _.get(remainingElement, "leafNodeCount", 0),
      coveredCount = _.get(coveredElement, "leafNodeCount", 0);

    if (_.isEqual(tableDataType, HEADER_DATA)) {
      remainingElementNodes = getPlannerElementNodes({
        elementSet: remainingElement,
        subject,
        curriculumType,
        isInterdisciplinary: true,
      });

      // we need leaf + parent nodes when displaying the actual remaining nodes
      // but for counts, we only consider leaf nodes and parents can't be tagged directly
      const remainingElementLeafNodes = _.filter(remainingElementNodes, [
        "isLeaf",
        true,
      ]);

      // not directly filtering leaf nodes since the logic will be different in PYP
      const coveredElementNodes = getPlannerElementNodes({
        elementSet: coveredElement,
        subject,
        curriculumType,
        isInterdisciplinary: true,
      });

      const coveredElementLeafNodes = _.filter(coveredElementNodes, [
        "isLeaf",
        true,
      ]);

      remainingCount = _.size(remainingElementLeafNodes) ?? remainingCount;
      coveredCount = _.size(coveredElementLeafNodes) ?? coveredCount;

      if (_.isEqual(curriculumType, CURRICULUM_TYPE_PYP)) {
        // we only need count of remaining leaf nodes
        // concating remaining and covered since one of these sets must have the rootNode
        const rootNode = _.find(
          _.concat(remainingElementNodes, coveredElementNodes),
          item => !item.parent
        );

        const levels = rootNode?.levels ?? [];

        const remainingLeafNodes = _.filter(
          remainingElementNodes,
          item => item.levelId == levels[levels.length - 1]?.id || item.isLeaf
        );
        const coveredLeafNodes = _.filter(
          coveredElementNodes,
          item => item.levelId == levels[levels.length - 1]?.id || item.isLeaf
        );

        remainingValue = getElementFilterValueMemoize({
          fieldObj,
          fieldUID,
          valueKeys: _.map(remainingLeafNodes, "id"),
          resolvedValue: {
            benchmarks: remainingElementNodes,
          },
        });

        remainingCount = _.size(remainingLeafNodes);
        coveredCount = _.size(coveredLeafNodes);

        // subject is parent, but we need subject's child nodes
        remainingValue = _.get(remainingValue, "[0].children", []);
      } else {
        // only need ids to pass to getElementOptions
        remainingElementNodes = _.map(remainingElementNodes, "id");
      }
    }

    const isSubjectMapped = _.includes(unitPlanSubjects, subjectId);

    const templateId = _.get(unitPlan, "templateId");
    switch (tableDataType) {
      case HEADER_DATA: {
        return {
          label,
          fieldUID,
          params: { subject: subjectName },
          type: elementType,
          coveredCount,
          remainingCount,
          viewType,
          subject,
          subjectId,
          fieldObj,
          remainingElementNodes,
          remainingValue,
        };
      }
      case BODY_DATA: {
        return {
          label,
          fieldUID,
          actualUID: _.get(itemObj, "fieldUID", ""),
          subjectId,
          subjectName,
          templateId,
          canEdit: canEdit && isSubjectMapped,
          disabledTooltip: isSubjectMapped
            ? "unitPlan:can_not_be_configured_for_unit_template"
            : "unitPlan:unit_not_mapped_to_subject_with_subject_name",
          params: { subject: subjectName },
          type: elementType,
          insightPlannerElement: getInsightPlannerElementData({
            itemObj: updatedItemObj,
            fieldObj,
            elementType,
            unitPlan,
            curriculumType,
            subjectIds: [subjectId],
          }),
          viewType,
        };
      }
      default: {
        return {};
      }
    }
  });
};

/**
 * This function generates and returns the grid data Unit wise for planner elements
 * for elements mapped to subjectGroup.
 *
 * @param {Object} param0
 * @param {Object} param0.itemObj - insight item object (from courseOverview array from template)
 * @param {Array} param0.subjects - passed subject list
 * @param {Object} param0.unitPlan - passed unit plan
 * @param {String} param0.elementType - element type to be displayed
 * @param {Object} param0.yearlyInsight - myp insight of course
 * @param {Array} param0.yearlySchedule - yearly schedule of course
 */

const getOtherFieldsData = ({
  itemObj,
  fieldObj,
  subjects,
  elementType,
  unitPlan,
  yearlyInsight,
  curriculumType,
  tableDataType,
  canEdit,
}) => {
  const subjectIds = _.map(subjects, subject => subject.id);

  const { remainingElement, coveredElement } = getRemainingAndCoveredElements({
    elementType,
    curriculumType,
    yearlyInsight,
  });

  let remainingValue;
  let params = {};
  const type = elementType;

  const fieldUID = _.get(itemObj, "fieldUID", "");
  const viewType = _.get(fieldObj, "viewType", "");

  const subjectGroup = _.get(subjects, "[0]subjectGroup", "");
  params = { subjectGroup: _.get(subjectGroup, "name", "") };

  const remainingCount = _.get(remainingElement, "leafNodeCount", 0);
  const coveredCount = _.get(coveredElement, "leafNodeCount", 0);

  const label = itemObj.label;

  let remainingElementNodes = [];

  if (_.isEqual(tableDataType, HEADER_DATA)) {
    remainingElementNodes = getPlannerElementNodes({
      curriculumType,
      elementSet: remainingElement,
    });

    if (_.isEqual(curriculumType, CURRICULUM_TYPE_PYP)) {
      remainingValue = getElementFilterValue({
        valueKeys: _.map(remainingElementNodes, "id"),
        fieldUID: itemObj.fieldUID,
        resolvedValue: {
          // resolved value is only for ATLs and ignored for others
          atls: remainingElementNodes,
        },
      });
    } else remainingElementNodes = _.map(remainingElementNodes, "id");
  }

  const subjectId = _.first(subjects)?.id;

  const templateId = _.get(unitPlan, "templateId");

  switch (tableDataType) {
    case HEADER_DATA: {
      return {
        label,
        fieldUID,
        params,
        type,
        coveredCount,
        remainingCount,
        viewType,
        fieldObj,
        remainingElementNodes,
        remainingValue,
      };
    }
    case BODY_DATA: {
      return {
        label,
        fieldUID,
        params,
        type,
        templateId,
        canEdit,
        disabledTooltip: "unitPlan:can_not_be_configured_for_unit_template",
        subjectId,
        insightPlannerElement: getInsightPlannerElementData({
          itemObj,
          fieldObj,
          unitPlan,
          curriculumType,
          subjectIds,
        }),
        viewType,
      };
    }
    default: {
      return {};
    }
  }
};

/**
 * This function generates the Unit wise grid data structure using subjects, yearly insight and
 * yearly schedule. The data structure later on will be consumed by the local Grid Body
 * component .
 *
 * @param {Object} param0
 * @param {Array} param0.subjects - subjects of queried course
 * @param {String} param0.curriculumType - curriculum Type
 * @param {Object} param0.yearlyInsight - queried myp yearly insight contains element set covered
 * @param {Array} param0.yearlySchedule - yearly schedule of queried course
 */

const getData = ({
  subjects,
  yearlyInsight,
  curriculumType,
  tableDataType,
  unitPlan,
  courseOverviewFields,
}) => {
  let plannerElementsData = [];

  _.forEach(courseOverviewFields, item => {
    // interdisciplinary fields are only displayed under their respective disciplinary field
    if (!_.get(item, "shouldDisplay", true)) return;

    const fieldObj = _.isEqual(tableDataType, HEADER_DATA)
      ? _.get(item, "gradeFieldObj", {})
      : _.get(item, ["fieldObjects", _.get(unitPlan, "templateId")]);

    const elementType = _.get(
      fieldObj,
      "config.plannerElementConfig.type",
      _.get(item, "gradeFieldObj.config.plannerElementConfig.type", {})
    );

    const itemObj = {
      ...item,
      elementType,
    };

    const canEdit = !_.isEmpty(fieldObj);

    if (
      isSubjectStandard({ elementType }) ||
      _.has(item, "interdisciplinaryUID")
    ) {
      const interdisciplinaryFieldsData = getInterdisciplinaryFieldsData({
        itemObj,
        fieldObj,
        subjects,
        elementType,
        unitPlan,
        yearlyInsight,
        curriculumType,
        tableDataType,
        canEdit,
      });
      plannerElementsData = [
        ...plannerElementsData,
        ...interdisciplinaryFieldsData,
      ];
    } else {
      let otherFieldsData = {};

      otherFieldsData = getOtherFieldsData({
        itemObj,
        fieldObj,
        subjects,
        elementType,
        unitPlan,
        yearlyInsight,
        curriculumType,
        tableDataType,
        canEdit,
      });

      if (!_.isEmpty(otherFieldsData))
        plannerElementsData = [...plannerElementsData, otherFieldsData];
    }
  });

  switch (tableDataType) {
    case HEADER_DATA: {
      return {
        plannerElementsHeaderData: plannerElementsData,
      };
    }
    case BODY_DATA: {
      return {
        unitPlan,
        plannerElementsData,
      };
    }
  }
};

const generateCourseOverviewGridData = ({
  subjects,
  yearlyInsight,
  yearlySchedule,
  curriculumType,
  courseOverviewFields,
}) => {
  const headerData = getData({
    subjects,
    yearlyInsight,
    curriculumType,
    courseOverviewFields,
    tableDataType: HEADER_DATA,
  });

  let gridData = [headerData];

  _.forEach(yearlySchedule, unitPlan => {
    const bodyData = getData({
      subjects,
      yearlyInsight,
      curriculumType,
      unitPlan,
      courseOverviewFields,
      tableDataType: BODY_DATA,
    });

    gridData = [...gridData, bodyData];
  });

  return gridData;
};

export const generateCourseOverviewGridDataMemoized = _.memoize(
  params => generateCourseOverviewGridData(params),
  params => JSON.stringify(params)
);

const getSubjectsByCurriculum = ({
  courseId,
  curriculumType,
  courseGradeFilters,
}) => {
  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters,
  });
  switch (curriculumType) {
    // subjects are not mapped to class in PYP
    case CURRICULUM_TYPE_PYP: {
      const primaryGradeId = _.get(courseDetails, "primaryGrade.id", "");
      const primaryGrade = _.find(_.get(courseDetails, "grades", []), {
        id: primaryGradeId,
      });
      return _.get(primaryGrade, "subjects", []);
    }

    default:
      return _.get(courseDetails, "subjects", []);
  }
};

export const getCourseOverviewDownload = ({ t }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const curriculumProgramId = _.get(
      state,
      "platform.currentCurriculumProgram.id",
      null
    );

    const organizationId = _.get(state, "login.userInfo.org_id", "");
    const orgName = _.get(
      getOrganizationDetailsFromCache(organizationId),
      "name",
      ""
    );
    const courseId = _.get(state, "teacher.selected_class.selected_course", "");
    const academicYearId = getSelectedAcademicYearId(state);
    const courseGradeFilters = {
      academicYearId,
    };

    const courseTitle = _.get(
      getCourseDetailsFromCache({ id: courseId, courseGradeFilters }),
      "title",
      ""
    );

    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      ""
    );
    const grades = _.get(state, "teacher.selected_class.selected_grades", null);

    const curriculumSubjects = getSubjectsByCurriculumMemoized({
      courseId,
      curriculumType,
      courseGradeFilters,
    });

    const subjects = getArchivedSortedSubjectListMemoized({
      subjectList: curriculumSubjects,
    });

    const attachmentName = `${orgName} - ${courseTitle} - ${t(
      "common:course_overview"
    )} - ${moment().format("D MMMM YYYY")}`;

    const fileId = generateRandomId();

    const filters = {
      curriculumProgramId,
      academicYearId,
    };
    if (_.isEqual(curriculumType, CURRICULUM_TYPE_PYP)) {
      filters["subjects"] = _.map(subjects, subject => subject.id);
      filters["grades"] = grades;
    }

    const variables = {
      id: courseId,
      filters,
    };

    try {
      const attachment = {
        id: fileId,
        type: "FILE",
        name: attachmentName,
        mimeType: "application/vnd.ms-excel",
      };
      dispatch(
        setProgress({
          id: fileId,
          progress: 0,
          attachment,
          status: "SAVE",
          type: "FILE",
        })
      );
      const result = await client.query({
        query: getCourseOverviewDownloadQuery,
        variables,
        fetchPolicy: "network-only",
      });

      const planningInsightDownloadResult = _.get(
        result,
        ["data", "node", "courseOverviewDownload"],
        {}
      );

      if (_.get(planningInsightDownloadResult, "isSuccess", false)) {
        if (_.isNull(planningInsightDownloadResult.url)) {
          dispatch(removeProgressElement({ id: fileId }));
          dispatch(
            setToastMsg({
              msg: "toastMsgs:file_does_not_exist",
              type: "warning",
              position: "toast-top-center",
            })
          );
        } else {
          attachment.url = planningInsightDownloadResult.url;
          dispatch(downloadFromUrl({ attachment, shouldAskForSave: true }));
        }
      } else {
        dispatch(removeProgressElement({ id: fileId }));
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-top-center",
          })
        );
      }
    } catch (e) {
      dispatch(removeProgressElement({ id: fileId }));

      if (e.response) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-top-center",
          })
        );
      } else if (e.networkError) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:no_internet_connection",
            type: "warning",
            position: "toast-top-center",
          })
        );
      }
    }
  };
};

export const getSubjectsByCurriculumMemoized = _.memoize(
  params => getSubjectsByCurriculum(params),
  props => JSON.stringify(props)
);

const REDUCER_HANDLERS = {
  [UPDATE_FILTERS]: (state, action) => {
    const params = action.data;

    _.forEach(_.keys(params), key => {
      state = update(state, {
        filters: {
          [key]: { $set: params[key] },
        },
      });
    });

    return state;
  },
};

const initialState = {
  filters: { columnOptions: [], lastCourseId: null, fieldUIDs: [] },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
